import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import type * as monaco from 'monaco-editor';
import monacoEditor, { getMonacoLanguageFromMime } from './monacoEditor';
import { EditorProps } from '../types';

type MonacoEditorInstance = {
  instance: monaco.editor.IStandaloneCodeEditor;
  monaco: typeof monaco;
};

const DefaultEditor = ({ ref, value, colormode, mime }: EditorProps) => {
  const htmlRef = useRef<HTMLDivElement>(null);
  const monacoRef = useRef<MonacoEditorInstance>(undefined);
  const initRef = useRef<boolean>(false);
  const [mounted, setMounted] = useState(false);

  const getErrors = useCallback(() => {
    if (!monacoRef.current) {
      return [];
    }
    const { monaco, instance } = monacoRef.current;
    const model = instance.getModel();
    const markers = monaco.editor.getModelMarkers({
      resource: model?.uri,
    });
    const errors = markers.filter(
      (m) => m.severity === monaco.MarkerSeverity.Error
    );
    return errors;
  }, []);

  useImperativeHandle(
    ref,
    () => {
      return {
        getValue: async () => {
          return {
            raw: monacoRef.current?.instance.getValue(),
          };
        },
        hasError: async () => {
          return getErrors().length !== 0;
        },
      };
    },
    [mounted]
  );

  useEffect(() => {
    if (initRef.current || !htmlRef.current) {
      return;
    }
    initRef.current = true;
    monacoRef.current = monacoEditor({
      ref: htmlRef.current,
      options: {
        value,
        autoIndent: 'full',
        language: getMonacoLanguageFromMime(mime),
        automaticLayout: true,
        fontSize: 14,
        padding: {
          top: 12,
        },
      },
    });
    setMounted(true);
  }, []);

  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.instance.updateOptions({
        theme: colormode === 'dark' ? 'vs-dark' : 'vs',
      });
    }
  }, [colormode, mounted]);

  return (
    <div
      style={{
        maxWidth: '100%',
        width: '100%',
        maxHeight: '100vh',
        height: '100%',
      }}
      ref={htmlRef}
    ></div>
  );
};

export default DefaultEditor;
